import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as translator from '../../../../../utils/translator';
import { formatDateStamp } from '../../../../../utils/timestampFormatter';
import { TimelineEvent } from 'data-types';
import CareProfileCard from './CareProfileCard';
import { localeByLanguage } from '../careProfileAssets';



interface TreatmentCycleTimelineProps {
	upcoming: TimelineEvent[];
	past: TimelineEvent[];
	country: string;
	language: string;
	onOpenSummary?: (event: TimelineEvent) => void;
	onOpenBooking?: (event: TimelineEvent) => void;
}

const TreatmentCycleTimeline: React.FC<TreatmentCycleTimelineProps> = ({ upcoming, past, country, language, onOpenSummary, onOpenBooking }) => {
	const navigate = useNavigate();
	const [showAllPast, setShowAllPast] = useState(false);

	const title = translator.translate(language, 'my_account', 'CareProfile/Timeline/Title');

	if (upcoming.length === 0 && past.length === 0) {
		return (
			<CareProfileCard id="treatment-cycle-timeline-empty">
				<p className="text-[22px] font-medium text-care-plus-heading mb-2">{title}</p>
				<p className="text-sm text-[#99a3b2]">{translator.translate(language, 'my_account', 'CareProfile/Timeline/Empty')}</p>
			</CareProfileCard>
		);
	}

	const visiblePast = showAllPast ? past : past.slice(0, 2);
	const rows: Array<{ event: TimelineEvent; status: TimelineRowStatus }> = [
		...upcoming.map((event, index) => ({ event, status: rowStatus(event, index) })),
		...visiblePast.map((event) => ({ event, status: 'past' as TimelineRowStatus })),
	];

	return (
		<CareProfileCard id="treatment-cycle-timeline" className="flex flex-col">
			<p className="text-[22px] font-medium text-care-plus-heading">{title}</p>

			<div id="treatment-cycle-timeline-events" className="flex flex-col mt-5">
				{rows.map((row, index) => (
					<TimelineRow
						key={row.event.id}
						event={row.event}
						country={country}
						language={language}
						navigate={navigate}
						isLast={index === rows.length - 1}
						status={row.status}
						onOpenSummary={onOpenSummary}
						onOpenBooking={onOpenBooking}
					/>
				))}
			</div>

			{past.length > 2 && (
				<button
					id="treatment-cycle-timeline-show-all-toggle"
					type="button"
					onClick={() => setShowAllPast((current) => !current)}
					className="w-full h-[56px] rounded-[12px] bg-[#f9f7f1] border border-solid border-[rgba(245,146,0,0.2)] text-[16px] font-medium text-care-plus-orange mt-2 cursor-pointer hover:bg-[#f5efe4] transition-colors duration-200"
				>
					{translator.translate(language, 'my_account', showAllPast ? 'CareProfile/Timeline/ShowLess' : 'CareProfile/Timeline/ShowAll')}
				</button>
			)}
		</CareProfileCard>
	);
};

type TimelineRowStatus = 'now' | 'available' | 'upcoming' | 'past';

const rowStatus = (event: TimelineEvent, index: number): TimelineRowStatus => {
	if (index === 0) return 'now';
	if (event.kind === 'consultation') return 'available';
	return 'upcoming';
};

/**
 * Consultations are labelled by provider role; deliveries by whether they already shipped.
 * `event.title` from the server is a translation key, not display text.
 */
const descKeyFor = (event: TimelineEvent): string => {
	if (event.kind === 'consultation') {
		return event.providerRole === 'nutritionist'
			? 'CareProfile/Timeline/Desc/NutritionConsultation'
			: 'CareProfile/Timeline/Desc/Consultation';
	}
	if (event.kind === 'delivery') {
		return event.isPast ? 'CareProfile/Timeline/Desc/PastDelivery' : 'CareProfile/Timeline/Desc/Delivery';
	}
	return 'CareProfile/Timeline/Desc/Renewal';
};

const badgeLabelKey: Record<TimelineRowStatus, string> = {
	now: 'CareProfile/Timeline/Now',
	available: 'CareProfile/Timeline/Available',
	upcoming: 'CareProfile/Timeline/UpcomingLabel',
	past: 'CareProfile/Timeline/Past',
};

const badgeTextClass: Record<TimelineRowStatus, string> = {
	now: 'text-care-plus-orange-dark',
	available: 'text-[#72b33e]',
	upcoming: 'text-[#99a3b2]',
	past: 'text-[#99a3b2]',
};

interface TimelineRowProps {
	event: TimelineEvent;
	country: string;
	language: string;
	navigate: ReturnType<typeof useNavigate>;
	isLast: boolean;
	status: TimelineRowStatus;
	onOpenSummary?: (event: TimelineEvent) => void;
	onOpenBooking?: (event: TimelineEvent) => void;
}

const TimelineRow: React.FC<TimelineRowProps> = ({ event, country, language, navigate, isLast, status, onOpenSummary, onOpenBooking }) => {
	const isNow = status === 'now';
	const showDate = status !== 'available';
	const detailsMuted = status === 'upcoming' || status === 'past';

	const dotClass = isNow
		? 'w-[15px] h-[15px] border-[3px] border-solid border-white bg-[#ffb909]'
		: 'w-2.5 h-2.5 border-[3px] border-solid border-white bg-[#e7e4db]';

	const cardClass = isNow
		? 'bg-[#fcfbf9] border border-solid border-[rgba(255,185,9,0.3)]'
		: 'bg-white border border-solid border-[#e7e4db]';

	const dateLabel = formatDateStamp(event.date, {
		noDayString: true,
		showYear: false,
		useShortMonth: true,
		locale: localeByLanguage[language] || 'en-US',
	});

	return (
		<div id={`treatment-cycle-timeline-event-${event.id}`} className="flex gap-x-[26px] items-stretch">
			<div className="flex flex-col items-center pt-1.5">
				<span className={`rounded-full flex-shrink-0 ${event.danger ? 'bg-red-500' : dotClass}`} />
				{!isLast && <span className="w-px flex-1 bg-[#e7e4db] mt-1" />}
			</div>

			<div className="flex flex-col flex-1 min-w-0 pb-6">
				<div className="flex items-center gap-x-[13px] mb-1">
					<p className={`text-[11px] font-bold uppercase tracking-[1px] ${event.danger ? 'text-red-500' : badgeTextClass[status]}`}>
						{translator.translate(language, 'my_account', badgeLabelKey[status])}
					</p>
					{showDate && dateLabel && (
						<>
							<span className="text-[11px] text-[#99a3b2]">•</span>
							<p className="text-[11px] font-bold uppercase tracking-[1px] text-[#99a3b2]">{dateLabel}</p>
						</>
					)}
				</div>

				<div className={`flex flex-col rounded-[8px] px-[17px] py-[13px] ${cardClass}`}>
					<p className={`text-[15px] font-medium leading-[22px] ${event.danger ? 'text-red-500' : 'text-care-plus-heading'}`}>
						{translator.translate(language, 'my_account', event.title)}
						{status === 'past' && event.kind === 'delivery' ? ' ✓' : ''}
					</p>
					<p className="text-[12px] leading-[18px] text-[#616975] mt-1">
						{translator.translate(language, 'my_account', descKeyFor(event))}
					</p>
					{event.trackingNumber && (
						<p id={`treatment-cycle-timeline-event-${event.id}-tracking`} className="text-[12px] leading-[18px] text-[#99a3b2] mt-1">
							{translator.translate(language, 'my_account', 'CareProfile/Timeline/TrackingNumber')}: {event.trackingNumber}
						</p>
					)}
					{event.summary && (
						<p id={`treatment-cycle-timeline-event-${event.id}-summary`} className="text-[12px] leading-[18px] text-[#616975] mt-2">
							{event.summary}
						</p>
					)}
					{event.orderHashID && (
						<button
							id={`treatment-cycle-timeline-event-${event.id}-open-details`}
							type="button"
							onClick={() => navigate(`/${country}/account/my-orders/${event.orderHashID}/details`)}
							className={`self-start text-[12px] font-medium tracking-[1px] underline whitespace-nowrap border-none bg-transparent mt-2 cursor-pointer hover:opacity-70 transition-opacity ${detailsMuted ? 'text-[#99a3b2]' : 'text-care-plus-orange-dark'}`}
						>
							{translator.translate(language, 'my_account', 'CareProfile/Timeline/OpenDetails')}
						</button>
					)}

					{/* Upcoming consultation with no order to link to: offer the booking flow. */}
					{event.kind === 'consultation' && status !== 'past' && !event.orderHashID && onOpenBooking && (
						<button
							id={`treatment-cycle-timeline-event-${event.id}-open-booking`}
							type="button"
							onClick={() => onOpenBooking(event)}
							className={`self-start text-[12px] font-medium tracking-[1px] underline whitespace-nowrap border-none bg-transparent mt-2 cursor-pointer hover:opacity-70 transition-opacity ${detailsMuted ? 'text-[#99a3b2]' : 'text-care-plus-orange-dark'}`}
						>
							{translator.translate(language, 'my_account', 'CareProfile/Timeline/OpenDetails')}
						</button>
					)}

					{/* Spec: upcoming consultations link to details/booking (above); completed ones
					    open the consultation record. */}
					{event.kind === 'consultation' && status === 'past' && onOpenSummary && (
						<button
							id={`treatment-cycle-timeline-event-${event.id}-open-summary`}
							type="button"
							onClick={() => onOpenSummary(event)}
							className={`self-start text-[12px] font-medium tracking-[1px] underline whitespace-nowrap border-none bg-transparent mt-2 cursor-pointer hover:opacity-70 transition-opacity ${detailsMuted ? 'text-[#99a3b2]' : 'text-care-plus-orange-dark'}`}
						>
							{translator.translate(language, 'my_account', 'CareProfile/Timeline/OpenSummary')}
						</button>
					)}

				</div>
			</div>
		</div>
	);
};

export default TreatmentCycleTimeline;
